import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { lazy } from 'react';
import { RequireSession } from './auth/Guard';
import { Attention } from './screens/Attention';
import {
  AcceptInvitation,
  CompleteReset,
  Login,
  MfaRequired,
  RequestReset,
  VerifyEmail,
} from './screens/Auth';
import { Mfa } from './screens/Mfa';
import { Placeholder } from './shell/Placeholder';
import { GLOBAL, LANDING, PROPERTY } from './shell/routes';
import { Shell } from './shell/Shell';

/**
 * Everything past the landing screen loads when it is first opened.
 *
 * The weight budget in `performance.spec.ts` is for the first load, and the
 * first load is sign-in or Attention. Shipping the document library, the
 * onboarding stepper and every dialog to somebody reading one Attention list
 * on a phone in a parkade is the bytes that budget exists to stop. Attention
 * and the entry routes stay in the main bundle so the screens the timings
 * measure never wait on a second request; the shell's Suspense boundary covers
 * the rest, inside the rail, so navigating never blanks the frame.
 */
function screen<K extends string>(load: () => Promise<Record<K, React.ComponentType>>, name: K) {
  return lazy(() => load().then((module) => ({ default: module[name] })));
}

const AcceptedRisk = screen(() => import('./screens/AcceptedRisk'), 'AcceptedRisk');
const MyCalendar = screen(() => import('./screens/Calendar'), 'MyCalendar');
const PropertyCalendar = screen(() => import('./screens/Calendar'), 'PropertyCalendar');
const Export = screen(() => import('./screens/Export'), 'Export');
const InspectionCapture = screen(() => import('./screens/Inspections'), 'InspectionCapture');
const InspectionResult = screen(() => import('./screens/Inspections'), 'InspectionResult');
const MyInspections = screen(() => import('./screens/Inspections'), 'MyInspections');
const NotificationSettings = screen(
  () => import('./screens/NotificationSettings'),
  'NotificationSettings',
);
const Notifications = screen(() => import('./screens/Notifications'), 'Notifications');
const ActionDetail = screen(() => import('./screens/DetailScreens'), 'ActionDetail');
const AssetDetail = screen(() => import('./screens/DetailScreens'), 'AssetDetail');
const ObligationDetail = screen(() => import('./screens/DetailScreens'), 'ObligationDetail');
const UserDetail = screen(() => import('./screens/DetailScreens'), 'UserDetail');
const VendorDetail = screen(() => import('./screens/DetailScreens'), 'VendorDetail');
const DocumentDetail = screen(() => import('./screens/DocumentDetail'), 'DocumentDetail');
const ConflictResolution = screen(() => import('./screens/EvidenceWorkflow'), 'ConflictResolution');
const DocumentRequests = screen(() => import('./screens/EvidenceWorkflow'), 'DocumentRequests');
const EvidenceReview = screen(() => import('./screens/EvidenceWorkflow'), 'EvidenceReview');
const EvidenceStates = screen(() => import('./screens/EvidenceWorkflow'), 'EvidenceStates');
const IssueWorkspace = screen(() => import('./screens/IssueWorkspace'), 'IssueWorkspace');
const OnboardingEvidence = screen(() => import('./screens/Onboarding'), 'OnboardingEvidence');
const OnboardingFindings = screen(() => import('./screens/Onboarding'), 'OnboardingFindings');
const OnboardingStart = screen(() => import('./screens/Onboarding'), 'OnboardingStart');
const Details = screen(() => import('./screens/PropertyScreens'), 'Details');
const Health = screen(() => import('./screens/PropertyScreens'), 'Health');
const People = screen(() => import('./screens/PropertyScreens'), 'People');
const Portfolio = screen(() => import('./screens/PropertyScreens'), 'Portfolio');
const Actions = screen(() => import('./screens/Registers'), 'Actions');
const Assets = screen(() => import('./screens/Registers'), 'Assets');
const Capital = screen(() => import('./screens/Registers'), 'Capital');
const Evidence = screen(() => import('./screens/Registers'), 'Evidence');
const MyWork = screen(() => import('./screens/Registers'), 'MyWork');
const Obligations = screen(() => import('./screens/Registers'), 'Obligations');
const Reports = screen(() => import('./screens/Reports'), 'Reports');
const Search = screen(() => import('./screens/Search'), 'Search');
const CapitalPlanDetail = screen(() => import('./screens/CapitalScreens'), 'CapitalPlanDetail');
const Vendors = screen(() => import('./screens/CapitalScreens'), 'Vendors');
const PropertyInspections = screen(
  () => import('./screens/InspectionManagement'),
  'PropertyInspections',
);
const Retention = screen(() => import('./screens/Retention'), 'Retention');

/**
 * The route tree.
 *
 * Two halves that behave differently, and the split is the point.
 *
 * **The entry routes render without a session** — sign-in, reset, invitation
 * acceptance, the MFA explanation. They sit outside the shell because the
 * shell's rail is drawn from a role and there is no role yet. A sign-in page
 * that renders a navigation has already started answering questions about the
 * product for somebody who has not identified themselves.
 *
 * **Everything else is behind `RequireSession`**, which asks the API rather
 * than inspecting the token: a token that parses is not a session that works,
 * because the subject may have been deactivated or their membership revoked.
 *
 * Switching property always lands on Attention (F02) rather than keeping the
 * current sub-route. Carrying the sub-route over is the intuitive behaviour and
 * the wrong one: it silently shows one building's screen for another building.
 */

/**
 * Which component owns which 9B2 screen id.
 *
 * A map rather than a chain of ternaries: ten screens landed at once and the
 * chain was already three deep. The map keys are the traceability ids, so a
 * screen with no entry falls through to the placeholder — visibly unbuilt
 * rather than silently missing.
 */
const PROPERTY_SCREENS: Record<string, React.ReactElement> = {
  'B1-03': <Attention />,
  'no-crop:calendar': <PropertyCalendar />,
  'B3-23': <Reports />,
  'B2-07': <Evidence />,
  'B2-11': <Assets />,
  'B2-13': <Obligations />,
  'B3-15': <Actions />,
  'B3-18': <Capital />,
  'B1-05': <Details />,
  'B1-06': <Health />,
};

const GLOBAL_SCREENS: Record<string, React.ReactElement> = {
  'B1-02': <Portfolio />,
  'B3-17': <MyWork />,
  'B3-24': <People />,
  'B5-01': <Search />,
  'B3-25': <Notifications />,
};

export function App() {
  return (
    <BrowserRouter>
      <Routes>
        {/* B1-01 and B5-08. No shell: there is no role to draw a rail from. */}
        <Route path="/login" element={<Login />} />
        <Route path="/reset" element={<RequestReset />} />
        <Route path="/reset/complete" element={<CompleteReset />} />
        <Route path="/invite/:token" element={<AcceptInvitation />} />
        <Route path="/email/verify" element={<VerifyEmail />} />
        <Route path="/mfa-required" element={<MfaRequired />} />
        {/* Outside the shell like the other entry routes: a session that
            cannot satisfy assurance cannot load a rail either, and the API
            would refuse every request the shell makes. */}
        <Route path="/mfa" element={<Mfa />} />

        <Route element={<RequireSession>{({ me }) => <Shell me={me} />}</RequireSession>}>
          {GLOBAL.map((s) => (
            <Route
              key={s.id}
              path={s.path}
              element={GLOBAL_SCREENS[s.id] ?? <Placeholder screen={s} />}
            />
          ))}
          <Route path="/my-work/calendar" element={<MyCalendar />} />
          <Route path="/settings/notifications" element={<NotificationSettings />} />
          <Route path="/admin/export" element={<Export />} />
          <Route path="/admin/retention" element={<Retention />} />
          <Route path="/admin/users/:userId" element={<UserDetail />} />
          <Route path="/onboarding/new" element={<OnboardingStart />} />
          {/* Outside /p/:propertyId deliberately. An inspector reaches work
              through an assignment, never through a property, and a route
              nested under a building would make the building the way in. */}
          <Route path="/inspections" element={<MyInspections />} />
          <Route path="/inspections/:inspectionId">
            {/* The deck draws capture and result as two screens (B3-20,
                B3-21) and they are two acts: answering an item and fixing the
                whole inspection do not belong under the same thumb. The bare
                route lands on capture, which is where an inspector arrives
                from their list. */}
            <Route index element={<Navigate to="capture" replace />} />
            <Route path="capture" element={<InspectionCapture />} />
            <Route path="result" element={<InspectionResult />} />
          </Route>
          <Route path="/p/:propertyId">
            <Route index element={<Navigate to={LANDING} replace />} />
            {/* Detail and workflow routes. Nested under the property, because
                every one of them is about a building and losing that context
                is how somebody reads the wrong strata's record. */}
            <Route path="issues/:subjectType/:subjectId" element={<IssueWorkspace />} />
            <Route path="assets/:assetId" element={<AssetDetail />} />
            <Route path="obligations/:obligationId" element={<ObligationDetail />} />
            <Route path="actions/:actionId" element={<ActionDetail />} />
            <Route path="inspections" element={<PropertyInspections />} />
            <Route path="capital/:planId" element={<CapitalPlanDetail />} />
            <Route path="vendors" element={<Vendors />} />
            <Route path="vendors/:vendorId" element={<VendorDetail />} />
            <Route path="risks/:riskId" element={<AcceptedRisk />} />
            <Route path="evidence/review" element={<EvidenceReview />} />
            <Route path="evidence/states" element={<EvidenceStates />} />
            <Route path="evidence/requests" element={<DocumentRequests />} />
            <Route path="evidence/conflicts" element={<ConflictResolution />} />
            <Route path="evidence/conflicts/:conflictId" element={<ConflictResolution />} />
            {/* W17. After the static evidence routes on purpose — the router
                ranks `evidence/review` above `evidence/:documentId` either way,
                and reading it in that order says so. */}
            <Route path="evidence/:documentId" element={<DocumentDetail />} />
            <Route path="onboarding/evidence" element={<OnboardingEvidence />} />
            <Route path="onboarding/findings" element={<OnboardingFindings />} />
            {PROPERTY.map((s) => (
              <Route
                key={s.id}
                path={s.path}
                // Screens replace their placeholder as their sprint lands. The
                // map stays the single source of the route tree, so §2's screen
                // map and this cannot drift apart.
                element={PROPERTY_SCREENS[s.id] ?? <Placeholder screen={s} />}
              />
            ))}
          </Route>
          <Route path="*" element={<Placeholder notFound />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}
